import { observer } from 'mobx-react'
import React, { useContext } from 'react'
import { Helmet } from 'react-helmet'
import { Navigate, useLocation } from 'react-router-dom'

import { AppShell } from '@/components/layout/AppShell'
import { Footer } from '@/components/layout/Footer'
import { UserStoreContext } from '@/stores/UserStore'
import { CookieHubConfig } from '@/utils/cookiehubConfig'

/**
 * PrivateRoute
 *
 * If a user is unauthenticated and attempts to access this route, they will be
 * redirected to the login page, otherwise we return the requested route.
 */
const PrivateRoute = observer(
  ({
    component: Component,
    restrictionCheck = null,
    skipFooter = false,
    skipLayout = false,
    ...rest
  }) => {
    // Context
    const { isAuthenticated, user } = useContext(UserStoreContext)
    const location = useLocation()

    // User is not authenticated - redirect to Login with current location passed along
    if (!isAuthenticated) {
      return (
        <Navigate
          to={{
            pathname: '/login',
          }}
          state={{ next: location?.pathname || null }}
        />
      )
    }

    // User is authenticated but does not have the correct role - redirect to dashboard
    if (user && restrictionCheck && !restrictionCheck(user)) {
      return (
        <Navigate
          to={{
            pathname: '/dashboard',
          }}
        />
      )
    }

    // Base case - render the requested component
    return (
      <>
        <Helmet script={[...CookieHubConfig()]} />
        <AppShell skipLayout={skipLayout}>
          <Component {...rest} />
        </AppShell>
        {!skipFooter && <Footer />}
      </>
    )
  },
)

export default PrivateRoute
